import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Pencil, Archive, RotateCcw, MapPin, Home } from "lucide-react";
import { toast } from "sonner";
import { CommunityOnboardingWizard } from "@/components/CommunityOnboardingWizard";
import TicketPhotoPolicyCard from "@/components/TicketPhotoPolicyCard";


const COMMUNITY_TYPES = [
  { value: "hoa", label: "HOA" },
  { value: "condo", label: "Condo Association" },
  { value: "coop", label: "Cooperative" },
  { value: "rental", label: "Rental building" },
  { value: "mixed", label: "Mixed-use" },
];

const empty = { name: "", address: "", units: "", community_type: "hoa",
                monthly_dues: "", reserve_fund: "", tagline: "", hero_image: "" };

export default function CommunitiesPage() {
  const { refresh: refreshCtx } = useProperty();
  const [items, setItems] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [wizardCommunity, setWizardCommunity] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/properties", { params: { include_archived: includeArchived } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load communities");
    } finally { setLoading(false); }
  }, [includeArchived]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", address: p.address || "", units: p.units || "",
      community_type: p.community_type || "hoa",
      monthly_dues: p.monthly_dues || "", reserve_fund: p.reserve_fund || "",
      tagline: p.tagline || "", hero_image: p.hero_image || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form,
        units: form.units ? Number(form.units) : undefined,
        monthly_dues: form.monthly_dues ? Number(form.monthly_dues) : undefined,
        reserve_fund: form.reserve_fund ? Number(form.reserve_fund) : undefined,
      };
      Object.keys(payload).forEach((k) => (payload[k] === "" || payload[k] == null) && delete payload[k]);
      if (editing) {
        await api.patch(`/admin/properties/${editing.id}`, payload);
        toast.success("Community updated");
        setDialogOpen(false);
        await load();
        refreshCtx?.();
      } else {
        const { data } = await api.post("/admin/properties", payload);
        toast.success("Community created — you now have access to it");
        setDialogOpen(false);
        await load();
        refreshCtx?.();
        if (data?.property) setWizardCommunity(data.property);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Save failed");
    } finally { setBusy(false); }
  };

  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.name}"? Historical financials remain, but the community disappears from switchers.`)) return;
    try {
      await api.delete(`/admin/properties/${p.id}`);
      toast.success("Archived");
      await load(); refreshCtx?.();
    } catch (e) { toast.error(apiErrorMessage(e) || "Archive failed"); }
  };

  const unarchive = async (p) => {
    try {
      await api.post(`/admin/properties/${p.id}/unarchive`);
      toast.success("Restored");
      await load(); refreshCtx?.();
    } catch (e) { toast.error(apiErrorMessage(e) || "Restore failed"); }
  };

  return (
    <div>
      <PageHeader eyebrow="Portfolio" title="Communities"
                  subtitle="Every property you manage. Add new communities, edit stats, or archive old ones." />

      <div className="flex items-center gap-3 mb-4">
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="community-add-btn">
          <Plus className="h-4 w-4 mr-1" /> New community
        </Button>
        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}
                 data-testid="community-include-archived" className="accent-orange-500" />
          Include archived
        </label>
        <div className="ml-auto text-xs text-zinc-500">{items.length} communit{items.length === 1 ? "y" : "ies"}</div>
      </div>

      {items.length === 0 && !loading ? (
        <Card className="p-8 text-center border-white/10" data-testid="communities-empty">
          <Building2 className="h-6 w-6 mx-auto text-zinc-500 mb-2" />
          <div className="text-sm text-zinc-400">No communities yet. Click <b>New community</b> to add your first one.</div>
        </Card>
      ) : (
        <>
          {/* Per-property policy tiles that key off the property switcher */}
          <div className="mb-4">
            <TicketPhotoPolicyCard />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((p) => (
            <Card key={p.id} className={"overflow-hidden border-white/10 " + (p.archived ? "opacity-60" : "")}
                  data-testid={`community-card-${p.id}`}>
              {p.hero_image ? (
                <img src={p.hero_image} alt="" className="w-full h-32 object-cover" />
              ) : (
                <div className="w-full h-32 bg-gradient-to-br from-orange-500/10 to-indigo-500/10 flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-zinc-600" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-100 truncate">{p.name}</div>
                    {p.tagline && <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{p.tagline}</div>}
                  </div>
                  {p.archived && <Badge variant="outline" className="border-amber-500/40 text-amber-300">Archived</Badge>}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                  <div className="flex items-center gap-1 text-zinc-400"><Home className="h-3 w-3" /> {p.units || "—"} units</div>
                  <div className="flex items-center gap-1 text-zinc-400 truncate"><MapPin className="h-3 w-3 shrink-0" /> {p.address || "—"}</div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)}
                          className="border-white/10 flex-1" data-testid={`community-edit-${p.id}`}>
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  {p.archived ? (
                    <Button size="sm" variant="outline" onClick={() => unarchive(p)}
                            className="border-emerald-500/30 text-emerald-300"
                            data-testid={`community-unarchive-${p.id}`}>
                      <RotateCcw className="h-3 w-3 mr-1" /> Restore
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => archive(p)}
                            className="text-zinc-400 hover:text-red-300"
                            data-testid={`community-archive-${p.id}`}>
                      <Archive className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
          </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[560px]" data-testid="community-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-orange-400" /> {editing ? "Edit community" : "New community"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              These stats power the finance dashboard, reserve forecast, and community switcher.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <div>
              <Label className="text-xs text-zinc-400">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Sunrise Ridge HOA" className="bg-white/[0.04] border-white/10" data-testid="community-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Tagline (optional)</Label>
              <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                     placeholder="Family-first hillside community" className="bg-white/[0.04] border-white/10" data-testid="community-tagline" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Address</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                        placeholder="123 Main St, Some City, CA 90000" className="bg-white/[0.04] border-white/10 text-sm" data-testid="community-address" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Community type</Label>
                <Select value={form.community_type} onValueChange={(v) => setForm({ ...form, community_type: v })}>
                  <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="community-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    {COMMUNITY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Units</Label>
                <Input type="number" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })}
                       placeholder="120" className="bg-white/[0.04] border-white/10" data-testid="community-units" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Monthly dues ($)</Label>
                <Input type="number" value={form.monthly_dues} onChange={(e) => setForm({ ...form, monthly_dues: e.target.value })}
                       placeholder="450" className="bg-white/[0.04] border-white/10" data-testid="community-dues" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Reserve fund ($)</Label>
                <Input type="number" value={form.reserve_fund} onChange={(e) => setForm({ ...form, reserve_fund: e.target.value })}
                       placeholder="250000" className="bg-white/[0.04] border-white/10" data-testid="community-reserve" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Hero image URL (optional)</Label>
              <Input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
                     placeholder="https://…"  className="bg-white/[0.04] border-white/10" data-testid="community-hero" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.name} className="bg-orange-500 hover:bg-orange-600" data-testid="community-save-btn">
              {busy ? "Saving…" : editing ? "Save changes" : "Create community"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CommunityOnboardingWizard
        open={!!wizardCommunity}
        onOpenChange={(v) => !v && setWizardCommunity(null)}
        community={wizardCommunity}
        onFinish={() => { setWizardCommunity(null); refreshCtx?.(); }}
      />
    </div>
  );
}
